
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapState } from 'vuex';
    import moment from 'moment';
    import Elgaimpfstoffe from '@/types/ElgaImpfstoffe';
    import Impfschema from '@/types/Impfschema';
    import Impfstoffe from '@/types/Impfstoffe';
    import Patient from '@/types/Patient';
    import Planimpfung from '@/types/Planimpfung';
    import Teilimpfungen from '@/types/Teilimpfungen';
    import { ImpfstoffeSelect } from '@/store/impfstoff';
    import { Resettable, VForm } from '@/types/Components';
    import { Teilimpfungsart } from '@/types/Teilimpfungsart';
    import { ValidationRules } from '@/store/types';

    @Component({
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate']),
            ...mapState('patient', ['patientData', 'dialogs', 'loaders']),
            ...mapState('impfstoff', ['impfstoffe', 'teilimpfungen', 'impfschema', 'elgaimpfstoffe']),
            ...mapState('impfstoff', { loadersImpfstoff: 'loaders' }),
        },
        methods: {
            ...mapActions('impfstoff', ['loadImpfstoffe']),
        },
    })
    export default class PlanNeueImpfung extends Vue {
        @Prop() visible!: boolean;

        public $refs!: {
            form: VForm;
            impfschema: Resettable;
            teilimpfung: Resettable;
            alleTeilimpfungen: Resettable;
        };

        public rules!: ValidationRules;
        public formatDate!: (input: string | null) => string | null;
        public parseDate!: (input: string | null) => string | null;
        public patientData!: Patient;
        public impfstoffe!: Impfstoffe[];
        public elgaimpfstoffe!: Elgaimpfstoffe[];
        public teilimpfungen!: Teilimpfungen[];
        public loadImpfstoffe!: (payload?: ImpfstoffeSelect) => void;

        public data = new Planimpfung();
        public filteredImpfschemen: Impfschema[] = [];
        public filteredTeilimpfungen: Teilimpfungen[] = [];

        public alleTeilimpfungen = false;

        public get hasTeilimpfungen(): boolean {
            return this.filteredTeilimpfungen.some((teilimpfung) => teilimpfung.isTeilimpfung);
        }

        public get impfstoff(): Impfstoffe {
            return this.impfstoffe.find((impfstoff: Impfstoffe) => {
                return impfstoff.impfstoff_id === this.data.impfstoff_id;
            }) as Impfstoffe;
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                this.reset();

                this.loadImpfstoffe({
                    impfschemen: true,
                    teilimpfungen: true,
                });
            }
        }

        public reset(): void {
            this.data = new Planimpfung();
            this.data.impfdatum_geplant = moment().format('YYYY-MM-DD');

            this.datumErrorMessages = [];
            this.datumFormatted = this.formatDate(this.data.impfdatum_geplant);

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            this.filteredImpfschemen = [];
        }

        public setImpfschema(): void {
            const { impfstoff_id } = this.data;

            const impfstoff_code = this.impfstoffe.find((impfstoff: Impfstoffe) => {
                return impfstoff.impfstoff_id === impfstoff_id;
            })?.impfstoff_code;

            const elgaimpfstoff = this.elgaimpfstoffe.find((impfstoff) => {
                return impfstoff.impfstoff_code === impfstoff_code;
            });

            const teilimpfungen = this.teilimpfungen.filter((teilimpfung: Teilimpfungen) => {
                return teilimpfung.impfstoff_id === impfstoff_id;
            });

            // this.filteredImpfschemen = elgaimpfstoff?.impfschemen ?? [];

            this.filteredImpfschemen = (elgaimpfstoff?.impfschemen ?? []).filter((impfschema) => {
                return teilimpfungen.some(({ impfschema_code }) => impfschema_code === impfschema.impfschema_code);
            });

            for (const ref of [this.$refs.impfschema]) {
                if (ref) {
                    ref.reset();
                }
            }

            if (this.filteredImpfschemen.length === 1) {
                this.data.impfschema_code = this.filteredImpfschemen[0].impfschema_code;
            } else {
                this.data.impfschema_code = '';
                this.alleTeilimpfungen = false;
            }

            this.setTeilimpfungen();
        }

        public setTeilimpfungen(): void {
            for (const ref of [this.$refs.teilimpfung, this.$refs.alleTeilimpfungen]) {
                if (ref) {
                    ref.reset();
                }
            }

            type comparator = (teilimpfung: Teilimpfungen) => boolean;

            const bySchema = (schema: string | null): comparator => (teilimpfung: Teilimpfungen): boolean => {
                return teilimpfung.impfstoff_id === this.data.impfstoff_id &&
                    teilimpfung.impfschema_code === schema;
            };

            let teilimpfungen = this.teilimpfungen.filter(bySchema(this.data.impfschema_code));

            if (teilimpfungen.length === 0) {
                teilimpfungen = this.teilimpfungen.filter(bySchema(null));
            }

            if (teilimpfungen.length === 0) {
                teilimpfungen.push(new Teilimpfungen(Teilimpfungsart.Einfachimpfung));
            }

            const auffrischung = teilimpfungen.some(({ teilimpfungsnr }: Teilimpfungen) => {
                return teilimpfungsnr === Teilimpfungsart.Auffrischung;
            });

            if (!auffrischung) {
                teilimpfungen.push(new Teilimpfungen(Teilimpfungsart.Auffrischung));
            }

            this.filteredTeilimpfungen = teilimpfungen;
        }

        public onClickAdd(closeDialog: boolean): void {
            if (!this.$refs.form.validate() || this.datumErrorMessages.length > 0) {
                return;
            }

            const planimpfungen: Planimpfung[] = [];

            const teilimpfungen = this.filteredTeilimpfungen.filter((teilimpfung: Teilimpfungen) => {
                return teilimpfung.isTeilimpfung;
            });

            const calcNextImpfdatum = (impfdatum: string | null, next: Teilimpfungen, prev?: Teilimpfungen): string => {
                const teilimpfung = prev ?? next;

                const abAlter = next.abstand2_alter ?? 0;
                const patientAlter = this.patientData.alter; // TODO: Alter durch Impfdatum berechnen

                if (next.teilimpfungsnr === Teilimpfungsart.Auffrischung &&
                    abAlter > 0 &&
                    typeof patientAlter === 'number' &&
                    patientAlter >= abAlter) {
                    return moment(impfdatum)
                        .add(next.abstand2_tage ?? 0, 'days')
                        .add(next.abstand2_wochen ?? 0, 'weeks')
                        .add(next.abstand2_monate ?? 0, 'months')
                        .add(next.abstand2_jahre ?? 0, 'years')
                        .format('YYYY-MM-DD');
                } else if (prev) {
                    return moment(impfdatum)
                        .add(teilimpfung.abstand_tage ?? 0, 'days')
                        .add(teilimpfung.abstand_wochen ?? 0, 'weeks')
                        .add(teilimpfung.abstand_monate ?? 0, 'months')
                        .add(teilimpfung.abstand_jahre ?? 0, 'years')
                        .format('YYYY-MM-DD');
                } else {
                    return moment(impfdatum).format('YYYY-MM-DD');
                }
            };

            const { impfdatum_geplant, impfstoff_id, impfschema_code, teilimpfungsnr } = this.data;
            const impfstoff = this.impfstoff;

            if (this.alleTeilimpfungen && teilimpfungen.length > 0) {
                teilimpfungen.forEach((teilimpfung: Teilimpfungen, index: number, items: Teilimpfungen[]) => {
                    const prevTeilimpfung = items[index - 1];
                    const lastPlanimpfung = planimpfungen[planimpfungen.length - 1];
                    const nextImpfdatum = lastPlanimpfung ? lastPlanimpfung.impfdatum_geplant : impfdatum_geplant;

                    planimpfungen.push(new Planimpfung({
                        teilimpfungsnr: teilimpfung.teilimpfungsnr,
                        impfstoff_id,
                        impfschema_code,
                        impfdatum_geplant: calcNextImpfdatum(nextImpfdatum, teilimpfung, prevTeilimpfung),
                        impfstoff_bezeichnung: impfstoff.bezeichnung,
                        impfstoff_impfung: impfstoff.impfung,
                    }));
                });
            } else {
                planimpfungen.push(new Planimpfung({
                    teilimpfungsnr,
                    impfstoff_id,
                    impfschema_code,
                    impfdatum_geplant,
                    impfstoff_bezeichnung: impfstoff.bezeichnung,
                    impfstoff_impfung: impfstoff.impfung,
                }));
            }

            this.$emit('click:add', planimpfungen);

            if (closeDialog) {
                this.$emit('close');
            } else {
                this.reset();
            }
        }

        public datumMenu = false;
        public datumFormatted: string | null = null;
        public datumErrorMessages: string[] = [];

        @Watch('data.impfdatum_geplant')
        public onChangeDatum(date: string | null): void {
            this.datumFormatted = this.formatDate(this.data.impfdatum_geplant);

            if (date !== null) {
                this.datumErrorMessages = [];

                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.data.impfdatum_geplant);
                    if (valid !== true) {
                        this.datumErrorMessages.push(valid as string);
                    }
                }
            }
        }
    }
